import {
  DayEntry,
  FrequencyUnit,
  ScheduleEntryState,
  Task,
  Timestamp,
  UpcomingPeriod,
  UpcomingPeriodTypes,
  WeekDays,
} from '@griply/types';
import {
  addMonths,
  addWeeks,
  addYears,
  eachMonthOfInterval,
  eachWeekOfInterval,
  eachYearOfInterval,
  endOfMonth,
  endOfWeek,
  endOfYear,
  isBefore,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
} from 'date-fns';

import { weekdaysNumberMap } from '../constants';
import { formatDateKey } from '../format-date-key';
import { getScheduleFrequency } from '../get-schedule-frequency';
import { getDueTaskPlanning, getTaskPlanning } from '../get-task-planning';
import { isBetween } from '../lib/date-fns';
import { getActiveSchedule } from '../schedules/get-active-schedule';
import { getScheduleByDate } from '../schedules/get-schedule-by-date';
import { getTimeframeSchedules } from '../schedules/get-timeframe-schedules';

export type GetUpcomingPeriodsOptions = {
  today: Timestamp;
  weekStartsOn: WeekDays;
};

export const getUpcomingPeriods = (
  task: Task,
  { today, weekStartsOn }: GetUpcomingPeriodsOptions,
): UpcomingPeriod[] => {
  if (!task.schedules?.length) {
    return [];
  }

  const activeSchedule = getActiveSchedule(task.schedules, today);
  const unit = activeSchedule
    ? getScheduleFrequency(activeSchedule)
    : FrequencyUnit.Week;

  const dateFnsOptions = { weekStartsOn: weekdaysNumberMap[weekStartsOn] };

  const startDate =
    unit === FrequencyUnit.Month
      ? startOfMonth(today)
      : unit === FrequencyUnit.Year
        ? startOfYear(today)
        : startOfWeek(today, dateFnsOptions);

  const endDate =
    unit === FrequencyUnit.Month
      ? endOfMonth(addMonths(startDate, 6))
      : unit === FrequencyUnit.Year
        ? endOfYear(addYears(startDate, 2))
        : endOfWeek(addWeeks(startDate, 6), dateFnsOptions);

  const dateRange = { startDate, endDate };

  const planning = getTaskPlanning(task, {
    ...dateRange,
    today,
    weekStartsOn,
  });

  const relevantDates = new Map<string, DayEntry>();

  planning.forEach((plannedOccurrence) => {
    const dateSchedule = getScheduleByDate(task.schedules, {
      date: plannedOccurrence.date,
      today,
    });
    const plannedOccurrenceInSchedule = dateSchedule?.plannedOccurrences?.find(
      ({ id }) => id === plannedOccurrence.id,
    );
    relevantDates.set(formatDateKey(plannedOccurrence.date), {
      ...plannedOccurrence,
      isUnplannedEntry: false,
      isGeneratedFrequencyEntry:
        !!dateSchedule?.frequency && !plannedOccurrenceInSchedule,
    });
  });

  const skippedPeriodKeys = new Set<string>();
  const timeframeSchedules = getTimeframeSchedules(task.schedules, dateRange);
  timeframeSchedules.forEach((schedule) => {
    schedule.entries.forEach((entry) => {
      if (!isBetween(entry.date, dateRange.startDate, dateRange.endDate)) {
        return;
      }

      if (entry.hidden && entry.state === ScheduleEntryState.Skip) {
        const periodStart =
          unit === FrequencyUnit.Month
            ? startOfMonth(entry.date)
            : unit === FrequencyUnit.Year
              ? startOfYear(entry.date)
              : startOfWeek(entry.date, dateFnsOptions);
        skippedPeriodKeys.add(formatDateKey(periodStart));
        return;
      }

      if (
        (entry.state === ScheduleEntryState.Complete && !!entry.value) ||
        entry.state === ScheduleEntryState.Failure ||
        entry.state === ScheduleEntryState.Skip
      ) {
        const key = formatDateKey(entry.date);
        const existing = relevantDates.get(key);
        if (existing?.isGeneratedFrequencyEntry) {
          existing.state = entry.state;
          return;
        }
        relevantDates.set(key, {
          id: entry.id,
          date: entry.date,
          timeslot: null,
          state: entry.state,
          hidden: false,
          isGeneratedFrequencyEntry: false,
          isUnplannedEntry: true,
        });
      }
    });
  });

  const sortedDates = Array.from(relevantDates.values()).sort(
    (a, b) => a.date.getTime() - b.date.getTime(),
  );

  const firstScheduleStartDate = startOfDay(
    new Date(
      Math.min(...task.schedules.map(({ startDate }) => startDate.getTime())),
    ),
  );

  const groups: UpcomingPeriod[] = [];
  const interval = { start: dateRange.startDate, end: dateRange.endDate };
  const relevantIntervals =
    unit === FrequencyUnit.Month
      ? eachMonthOfInterval(interval)
      : unit === FrequencyUnit.Year
        ? eachYearOfInterval(interval)
        : eachWeekOfInterval(interval, dateFnsOptions);

  relevantIntervals.forEach((periodStart) => {
    const periodEnd =
      unit === FrequencyUnit.Month
        ? endOfMonth(periodStart)
        : unit === FrequencyUnit.Year
          ? endOfYear(periodStart)
          : endOfWeek(periodStart, dateFnsOptions);

    const daysInPeriod = sortedDates.filter(({ date }) =>
      isBetween(date, periodStart, periodEnd),
    );

    const mappedDays = daysInPeriod.map((entry) => ({
      date: entry.date,
      entry,
    }));

    const activeScheduleForPeriod = getScheduleByDate(task.schedules, {
      date: periodStart,
      today,
    });

    const hasFrequencyTarget = !!activeScheduleForPeriod?.frequency?.count;

    const isFrequencyPeriodSkipped =
      hasFrequencyTarget && skippedPeriodKeys.has(formatDateKey(periodStart));

    // lead-in periods (before the first schedule ever starts) get no placeholder
    const isLeadInPeriod = isBefore(periodEnd, firstScheduleStartDate);

    if (hasFrequencyTarget && !mappedDays.length && !isLeadInPeriod) {
      if (!isFrequencyPeriodSkipped) {
        mappedDays.push({
          date: periodStart,
          entry: {
            id: `generated-${periodStart.getTime()}`,
            date: periodStart,
            timeslot: null,
            hidden: false,
            isGeneratedFrequencyEntry: true,
            isUnplannedEntry: false,
          },
        });
      }

      if (isFrequencyPeriodSkipped) {
        const hiddenSkipEntry = activeScheduleForPeriod?.entries.find(
          (entry) =>
            entry.hidden &&
            entry.state === ScheduleEntryState.Skip &&
            isBetween(entry.date, periodStart, periodEnd),
        );
        if (hiddenSkipEntry) {
          mappedDays.push({
            date: hiddenSkipEntry.date,
            entry: {
              id: hiddenSkipEntry.id,
              date: hiddenSkipEntry.date,
              timeslot: null,
              state: ScheduleEntryState.Skip,
              hidden: true,
              isGeneratedFrequencyEntry: true,
              isUnplannedEntry: false,
            },
          });
        }
      }
    }

    groups.push({
      date: periodStart,
      days: mappedDays,
      type: unit,
    });
  });

  const overduePlanning = getDueTaskPlanning(task, {
    date: today,
    today,
    weekStartsOn,
    uncompletedOnly: true,
  });

  const overdueEntries: DayEntry[] = overduePlanning
    .filter((entry) => isBefore(entry.date, dateRange.startDate))
    .map((plannedOccurrence) => ({
      ...plannedOccurrence,
      isGeneratedFrequencyEntry: false,
      isUnplannedEntry: false,
      hidden: false,
    }));

  if (overdueEntries.length) {
    groups.unshift({
      date: subDays(dateRange.startDate, 1),
      days: overdueEntries.map((entry) => ({ date: entry.date, entry })),
      type: UpcomingPeriodTypes.Overdue,
    });
  }

  return groups;
};
